// Turns the uploaded first-person shotgun frames (Gun0.png = pump forward,
// Gun01reload.png = pump racked back) into transparent sprites. Both sit on a
// flat olive-green background. We remove the background with a flood-fill from
// the borders (so only background-connected green is cleared — the gun itself
// stays fully opaque, no holes from interior greenish pixels), then crop the
// empty sky above the muzzle and the long forearms below. Both frames are
// cropped IDENTICALLY so they stay registered when alternated during a reload.
// Returns a data URL, or null on failure (gun falls back to procedural).

const KEY2 = 70 * 70; // green-distance² threshold for "background"
const CROP_TOP = 250; // trim empty sky above the muzzle
const CROP_BOT = 1080; // trim the forearms below the grip ("crop from bottom")
const DOWNSCALE = 0.5; // 1254px sources are large; half-res is plenty

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed: " + url));
    img.src = url;
  });
}

export async function processGunArt(url: string, key = true): Promise<string | null> {
  try {
    const img = await loadImage(url);
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    if (!W || !H) return null;

    const src = document.createElement("canvas");
    src.width = W;
    src.height = H;
    const sctx = src.getContext("2d", { willReadFrequently: true });
    if (!sctx) return null;
    sctx.drawImage(img, 0, 0);

    // Already-transparent art (key=false): skip the green key, just crop+scale.
    if (!key) return cropScale(src, W, H);

    const im = sctx.getImageData(0, 0, W, H);
    const d = im.data;
    const bg = [d[0], d[1], d[2]];
    const N = W * H;

    // green mask
    const green = new Uint8Array(N);
    for (let p = 0; p < N; p++) {
      const i = p * 4;
      const dr = d[i] - bg[0];
      const dg = d[i + 1] - bg[1];
      const db = d[i + 2] - bg[2];
      if (dr * dr + dg * dg + db * db < KEY2) green[p] = 1;
    }

    // flood-fill the background from every border green pixel
    const bgm = new Uint8Array(N);
    const stack: number[] = [];
    for (let x = 0; x < W; x++) {
      for (const p of [x, (H - 1) * W + x]) if (green[p] && !bgm[p]) ((bgm[p] = 1), stack.push(p));
    }
    for (let y = 0; y < H; y++) {
      for (const p of [y * W, y * W + W - 1]) if (green[p] && !bgm[p]) ((bgm[p] = 1), stack.push(p));
    }
    while (stack.length) {
      const p = stack.pop() as number;
      const x = p % W;
      const y = (p - x) / W;
      if (x > 0 && green[p - 1] && !bgm[p - 1]) ((bgm[p - 1] = 1), stack.push(p - 1));
      if (x < W - 1 && green[p + 1] && !bgm[p + 1]) ((bgm[p + 1] = 1), stack.push(p + 1));
      if (y > 0 && green[p - W] && !bgm[p - W]) ((bgm[p - W] = 1), stack.push(p - W));
      if (y < H - 1 && green[p + W] && !bgm[p + W]) ((bgm[p + W] = 1), stack.push(p + W));
    }
    for (let p = 0; p < N; p++) if (bgm[p]) d[p * 4 + 3] = 0; // only the background goes transparent
    sctx.putImageData(im, 0, 0);

    return cropScale(src, W, H);
  } catch {
    return null;
  }
}

// Crop off the empty top + the forearms, downscale; identical for every frame so
// they stay registered.
function cropScale(src: HTMLCanvasElement, W: number, H: number): string | null {
  const ch = CROP_BOT - CROP_TOP;
  const out = document.createElement("canvas");
  out.width = Math.round(W * DOWNSCALE);
  out.height = Math.round(ch * DOWNSCALE);
  const octx = out.getContext("2d");
  if (!octx) return null;
  octx.imageSmoothingEnabled = true;
  octx.drawImage(src, 0, CROP_TOP, W, ch, 0, 0, out.width, out.height);
  return out.toDataURL();
}
